import React, { useState, useEffect } from 'react';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function SSHKeysDashboard() {
  const [sshKeys, setSSHKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/ssh-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSSHKeys(data.ssh_keys || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching SSH keys:', error);
      setLoading(false);
    }
  };

  const createKey = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/ssh-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.get('name'),
          public_key: formData.get('public_key')
        })
      });

      if (response.ok) {
        setShowCreateModal(false);
        fetchKeys();
      } else {
        const error = await response.json();
        alert('Error: ' + (error.error || 'Failed to create SSH key'));
      }
    } catch (error) {
      alert('Error creating SSH key: ' + error.message);
    }
  };

  const deleteKey = async (keyId) => {
    if (!window.confirm('Delete this SSH key?')) return;
    try {
      await fetch(`${API_URL}/v1/ssh-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchKeys();
    } catch (error) {
      alert('Error deleting SSH key: ' + error.message);
    }
  };

  return (
    <Layout userType="customer">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">SSH Keys</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            + Add SSH Key
          </button>
        </div>

        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Fingerprint</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Public Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sshKeys.map((key) => (
                <tr key={key.id}>
                  <td className="px-6 py-4 text-white font-medium">{key.name}</td>
                  <td className="px-6 py-4 text-gray-400 font-mono text-sm">{key.fingerprint}</td>
                  <td className="px-6 py-4 text-gray-400 font-mono text-xs max-w-md truncate">
                    {key.public_key}
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-sm">
                    {new Date(key.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => deleteKey(key.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {sshKeys.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p className="mb-4">No SSH keys yet.</p>
              <p className="text-sm">Add your first SSH key to enable secure access to your instances.</p>
            </div>
          )}
        </div>

        <div className="mt-6 bg-blue-900 border border-blue-700 rounded-lg p-4">
          <h3 className="text-blue-200 font-medium mb-2">💡 How to use SSH Keys</h3>
          <ul className="text-blue-300 text-sm space-y-1">
            <li>• Upload your public SSH key (usually ~/.ssh/id_rsa.pub)</li>
            <li>• Select which keys to add when creating new instances</li>
            <li>• Keys are automatically injected into new VMs via cloud-init</li>
            <li>• Connect with: ssh ubuntu@your-instance-ip</li>
          </ul>
        </div>
      </div>

      {/* Create SSH Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Add SSH Key</h3>
            
            <form onSubmit={createKey}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="e.g., My Laptop"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Public Key</label>
                  <textarea
                    name="public_key"
                    required
                    rows="6"
                    placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQC... user@hostname"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white font-mono text-sm"
                  ></textarea>
                  <p className="text-gray-400 text-xs mt-1">
                    Paste your entire public key (usually from ~/.ssh/id_rsa.pub)
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Key
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default SSHKeysDashboard;
